/**
 * Tiny shadcn-AlertDialog-based replacement for native `window.confirm()`.
 *
 * Pattern:
 *   const [askConfirm, ConfirmUi] = useConfirm();
 *   const ok = await askConfirm({ title, description, confirmLabel, destructive });
 *   if (!ok) return;
 *   // ...
 *   return <>...{ConfirmUi}</>;
 *
 * BUG 06b — Global delete confirmation. Pass `kind: 'delete'` to render the
 * canonical bilingual copy: "Are you sure you want to delete this? This action
 * cannot be undone." (or the ES equivalent) plus a destructive Delete button.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DEFAULTS = {
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
  testId: 'confirm-dialog',
  kind: null, // 'delete' applies BUG 06b canonical copy
};

export function useConfirm() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  const ask = useCallback((overrides = {}) => {
    const merged = { ...DEFAULTS, ...overrides };
    // BUG 06b — Apply canonical delete copy when caller passes kind:'delete'.
    if (merged.kind === 'delete') {
      merged.title = overrides.title || t('common.deleteConfirm.title', { defaultValue: 'Are you sure you want to delete this?' });
      merged.description = overrides.description || t('common.deleteConfirm.body', { defaultValue: 'This action cannot be undone.' });
      merged.confirmLabel = overrides.confirmLabel || t('common.delete', { defaultValue: 'Delete' });
      merged.cancelLabel = overrides.cancelLabel || t('common.cancel', { defaultValue: 'Cancel' });
      merged.destructive = true;
      merged.testId = overrides.testId || 'delete-confirm';
    }
    setOpts(merged);
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, [t]);

  const close = (value) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  };

  const ui = (
    <AlertDialog
      open={open}
      onOpenChange={(next) => { if (!next) close(false); }}
    >
      <AlertDialogContent data-testid={opts.testId}>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid={`${opts.testId}-title`}>{opts.title}</AlertDialogTitle>
          {opts.description && (
            <AlertDialogDescription data-testid={`${opts.testId}-desc`}>{opts.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => close(false)} data-testid={`${opts.testId}-cancel`}>
            {opts.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => close(true)}
            data-testid={`${opts.testId}-confirm`}
            className={opts.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {opts.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [ask, ui];
}
